import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FieldType, Form, FormField, ModalId, useUIActions, Validatable } from "../../../../../features/ui"
import { Operation } from "../../../../operations"
import { DeliveryOrder } from "../../../state/types"
import { ReturnItemRow, INVOICE_IN_FLIGHT_MESSAGE, NoteGenerationType } from "../../types"
import { ReturnItemsTable } from "../return-items-table/returnItemsTable"
import { useActions } from "./actions"
import { TableName } from "../../../../../tables"
import { GenerateNoteModal } from "../../generate-note-modal/generateNoteModal"
import { FiscalDocument, hasSentInvoice } from "../../../../fiscal-documents"
import { HoardOrder, SaleOperation } from "../../../../sales"
import { hoardHelpers } from "../../../../sales/hooks/hoardHooks"
import { toList } from "../../../../../utils/listUtils"

type Props = {
    table: TableName
    operation: Operation
    deliveryOrders: DeliveryOrder[]
    fiscalDocuments: FiscalDocument[]
}

const buildItemRows = (
    operation: Operation,
    deliveryOrders: DeliveryOrder[]
): ReturnItemRow[] => {
    const deliveredByItem = new Map<string, number>()
    deliveryOrders.forEach(deliveryOrder => {
        deliveryOrder.items.forEach(item => {
            const delivered = item.deliveryStats?.delivered ?? 0
            if (delivered > 0) {
                deliveredByItem.set(item.itemId, (deliveredByItem.get(item.itemId) ?? 0) + delivered)
            }
        })
    })
    return Array.from(deliveredByItem.entries()).map(([itemId, delivered]) => {
        const operationItem = operation.items.find(operationItem => operationItem.id === itemId)!
        return {
            itemId,
            productId: operationItem.productId,
            lotId: operationItem.lotId,
            delivered,
            returnQuantity: 0,
            defectiveQuantity: 0,
            nonDefectiveQuantity: 0
        }
    })
}

export const ReturnItemsForm = (props: Props) => {
    const { table, operation, deliveryOrders, fiscalDocuments } = props

    const isHoard = table === TableName.HOARD_ORDERS
    const isFinancialHoard = hoardHelpers.isFinancialHoard(table, operation as SaleOperation)
    const hasInvoiceInFlight = isHoard && !!(operation as HoardOrder).invoiceInFlight

    // Ventas: se ofrece el tipo de nota si la factura ya se envió (un preview o una factura rechazada no cuentan).
    const hasInvoice = useMemo(
        () => hasSentInvoice(fiscalDocuments),
        [fiscalDocuments]
    )

    const navigate = useNavigate()

    const defaultRows = useMemo(() => buildItemRows(operation, deliveryOrders), [operation, deliveryOrders])

    const [itemRows, setItemRows] = useState<ReturnItemRow[]>(defaultRows)

    const { submitReturn } = useActions()

    const { openModal } = useUIActions()

    useEffect(() => {
        setItemRows(defaultRows)
    }, [defaultRows])

    const totalReturnQuantity = useMemo(
        () => itemRows.reduce((sum, item) => sum + item.returnQuantity, 0),
        [itemRows]
    )

    const hasNonDefectiveItems = useMemo(
        () => itemRows.some(item => item.nonDefectiveQuantity > 0),
        [itemRows]
    )

    // En acopios la nota fiscal solo corresponde a la cantidad ya facturada. En financiero se
    // devuelve (y cancela) la cantidad completa; en producto, solo la que no tiene defecto.
    const hasInvoicedQuantity = useMemo(
        () => isHoard && hoardHelpers.splitQuantitiesByInvoicing(
            operation as SaleOperation,
            itemRows.map(row => ({
                itemId: row.itemId,
                quantity: isFinancialHoard ? row.returnQuantity : row.nonDefectiveQuantity
            })),
            'return',
            hoardHelpers.getNetDeliveredByItem(deliveryOrders)
        ).invoiced.length > 0,
        [isHoard, isFinancialHoard, operation, itemRows, deliveryOrders]
    )

    const hasNoteTypeOption = useMemo(
        () => !hasInvoiceInFlight && !isFinancialHoard && hasNonDefectiveItems && (isHoard ? hasInvoicedQuantity : hasInvoice),
        [hasInvoiceInFlight, isFinancialHoard, hasNonDefectiveItems, isHoard, hasInvoicedQuantity, hasInvoice]
    )

    const fields: FormField[] = [
        ...(hasInvoiceInFlight ? [{
            name: 'invoiceInFlight',
            type: FieldType.TEXT,
            render: () => <Validatable validations={toList(INVOICE_IN_FLIGHT_MESSAGE)} expanded />
        }] : []),
        {
            name: 'items',
            type: FieldType.TABLE,
            render: () => (
                <ReturnItemsTable
                    operation={operation}
                    defaultRows={defaultRows}
                    onChange={setItemRows}
                />
            )
        }
    ]

    const onConfirmNote = (noteType: NoteGenerationType) => {
        return submitReturn(table, operation.id, itemRows, noteType)
    }

    // La NC se genera por la cantidad facturada: en financiero siempre es fiscal.
    const onSubmit = () => {
        if (hasInvoiceInFlight) return
        if (isFinancialHoard) {
            return onConfirmNote(NoteGenerationType.CREDIT_NOTE)
        }
        return hasNoteTypeOption
            ? openModal(ModalId.GENERATE_NOTE)
            : onConfirmNote(NoteGenerationType.ADJUSTMENT_NOTE)
    }

    const onCancel = () => navigate(-1)

    return (
        <>
            <Form
                formId="return-items-form"
                fields={fields}
                createLabel="Registrar Devolución"
                disableSubmit={totalReturnQuantity === 0 || hasInvoiceInFlight}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
            {hasNoteTypeOption && <GenerateNoteModal onConfirm={onConfirmNote} />}
        </>
    )
}
